import React, { useId } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
//  GHARPE LOGO COMPONENTS
//
//  Fix applied: SVG linearGradient IDs must be globally unique.
//  The original used hard-coded id="lg1" / id="lg2" — when two Logo instances
//  appeared on the same page (e.g. splash + nav), the second instance's
//  gradient referenced the first instance's <defs>, causing the wrong color
//  or a blank fill on some browsers.
//
//  useId() (React 18) generates a stable, unique ID per component instance.
//  If you are on React 17, replace useId() with a module-level counter:
//    let _id = 0;
//    const uid = () => `gp-${++_id}`;
//  and call uid() inside the component body.
// ─────────────────────────────────────────────────────────────────────────────

// Brand colors — aligned with the unified COLORS token in constants.js
const NAVY        = '#0F2D5C';
const NAVY_LIGHT  = '#1A4080';
const ORANGE      = '#FF5722';
const GOLD        = '#FBBF24';
const WHITE       = '#FFFFFF';

// ─── FULL LOGO (colored, with optional text) ─────────────────────────────────
export const GharPeLogo = React.memo(function GharPeLogo({ size = 56, showText = true }) {
  const uid  = useId();
  const bg   = `${uid}-bg`;
  const roof = `${uid}-roof`;

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="GharPe logo"
      >
        <defs>
          <linearGradient id={bg} x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={NAVY}/>
            <stop offset="100%" stopColor={NAVY_LIGHT}/>
          </linearGradient>
          <linearGradient id={roof} x1="0" y1="0" x2="56" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={GOLD}/>
            <stop offset="100%" stopColor={ORANGE}/>
          </linearGradient>
        </defs>
        {/* Background circle */}
        <rect width="56" height="56" rx="14" fill={`url(#${bg})`}/>
        {/* House body */}
        <rect x="14" y="30" width="28" height="18" rx="3" fill={WHITE} opacity="0.95"/>
        {/* Roof */}
        <path d="M8 30L28 10l20 20H8z" fill={`url(#${roof})`}/>
        {/* Door */}
        <rect x="22" y="36" width="12" height="12" rx="2" fill={NAVY}/>
        {/* Windows */}
        <rect x="14" y="32" width="6" height="6" rx="1" fill="#BFDBFE"/>
        <rect x="36" y="32" width="6" height="6" rx="1" fill="#BFDBFE"/>
        {/* Chimney */}
        <rect x="36" y="16" width="5" height="9" rx="1.5" fill={GOLD}/>
        {/* Door knob */}
        <circle cx="29" cy="42" r="1.2" fill={GOLD}/>
      </svg>

      {showText && (
        <div style={{ textAlign:'center' }}>
          <span style={{
            fontSize: size * 0.4,
            fontWeight: 900,
            color: NAVY,
            letterSpacing: -0.5,
            lineHeight: 1,
            display: 'block',
          }}>
            GharPe
          </span>
          <span style={{
            display: 'block',
            fontSize: size * 0.16,
            color: '#9CA3AF',
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginTop: 2,
          }}>
            घर पे
          </span>
        </div>
      )}
    </div>
  );
});

// ─── WHITE VARIANT (for dark / colored header backgrounds) ───────────────────
export const GharPeLogoWhite = React.memo(function GharPeLogoWhite({ size = 36 }) {
  const uid  = useId();
  const roof = `${uid}-roof-w`;

  return (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="GharPe logo"
        style={{ flexShrink: 0 }}
      >
        <defs>
          <linearGradient id={roof} x1="0" y1="0" x2="56" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={GOLD}/>
            <stop offset="100%" stopColor={ORANGE}/>
          </linearGradient>
        </defs>
        {/* Background — translucent on any dark bg */}
        <rect width="56" height="56" rx="14" fill="rgba(255,255,255,0.15)"/>
        {/* House body */}
        <rect x="14" y="30" width="28" height="18" rx="3" fill={WHITE} opacity="0.95"/>
        {/* Roof */}
        <path d="M8 30L28 10l20 20H8z" fill={`url(#${roof})`}/>
        {/* Door */}
        <rect x="22" y="36" width="12" height="12" rx="2" fill="rgba(255,255,255,0.25)"/>
        {/* Windows */}
        <rect x="14" y="32" width="6" height="6" rx="1" fill="#BFDBFE"/>
        <rect x="36" y="32" width="6" height="6" rx="1" fill="#BFDBFE"/>
        {/* Chimney */}
        <rect x="36" y="16" width="5" height="9" rx="1.5" fill={GOLD}/>
        {/* Door knob */}
        <circle cx="29" cy="42" r="1.2" fill={GOLD}/>
      </svg>

      <div>
        <div style={{
          color: WHITE,
          fontWeight: 900,
          fontSize: size * 0.5,
          letterSpacing: -0.4,
          lineHeight: 1,
        }}>
          GharPe
        </div>
        <div style={{
          color: 'rgba(255,255,255,0.65)',
          fontSize: size * 0.27,
          lineHeight: 1,
          marginTop: 2,
          letterSpacing: 1,
        }}>
          घर पे
        </div>
      </div>
    </div>
  );
});
